import React from 'react';
import Badge from './Badge';

// Filter chip row for list pages. Renders a wrapping row of toggle buttons,
// each optionally carrying a count. Selection is single-choice: the active
// chip's key (or null = nothing / "all"). Clicking the active chip deselects.
//
// ⚠️ COUNT CONTRACT: `count` on each chip MUST be computed by the CALLER from
// the FULL, UNFILTERED dataset — never from the already-filtered subset the
// active chip produces. A chip's job is to show "how many match me if I were
// picked", so counting the current (filtered) view undercounts every chip —
// each number shrinks to its intersection with the active filter (and the
// active chip's own count degenerates to the whole visible total). This
// component only RENDERS the numbers it
// is handed; getting them right is the page's responsibility.
// (See src/pages/JobCards.js hand-rolled chips — those count the filtered
// subset and are wrong; do not copy that math.)

const CHIP_BASE =
  'inline-flex items-center gap-2 min-h-[44px] px-3.5 rounded-full border text-sm font-medium ' +
  'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-canvas select-none';

const CHIP_ACTIVE = 'bg-accent-soft border-accent text-accent-hover dark:text-accent';
const CHIP_IDLE =
  'bg-surface border-edge text-content-secondary hover:bg-surface-2 hover:text-content';

export default function FilterChips({
  chips = [],
  active = null,
  onChange,
  allLabel,
  className = '',
}) {
  const renderChip = (key, label, count, tone) => {
    const isActive = active === key;
    return (
      <button
        key={key ?? '__all__'}
        type="button"
        aria-pressed={isActive}
        onClick={() => onChange(isActive ? null : key)}
        className={`${CHIP_BASE} ${isActive ? CHIP_ACTIVE : CHIP_IDLE}`}
      >
        <span>{label}</span>
        {count !== undefined && (
          <Badge
            tone={isActive ? 'accent' : tone || 'gray'}
            size="sm"
            className="tabular-nums"
            dir="ltr"
          >
            {count}
          </Badge>
        )}
      </button>
    );
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {allLabel != null && renderChip(null, allLabel)}
      {chips.map((c) => renderChip(c.key, c.label, c.count, c.tone))}
    </div>
  );
}
